import { motion } from 'framer-motion';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { BottomSheet, useToast } from '../../components';
import { useActions, useAppData } from '../../data';
import { formatCurrency } from '../../lib/currency';
import { springQuick, tapScale } from '../../tokens';

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Tap-to-apply sheet for Quick Add presets. Lists every preset; tapping
 * one calls applyQuickPreset and closes the sheet. Manage / edit lives
 * elsewhere (Settings → Quick Add Presets).
 */
export function QuickApplySheet({ open, onClose }: Props) {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();

  const presets = (data.profile.quickPresets ?? [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const currency = data.profile.currency;

  const apply = (id: string, label: string) => {
    const tx = actions.applyQuickPreset(id);
    if (tx) {
      toast.show({ message: `Added: ${label}`, kind: 'success' });
    }
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Quick add">
      <div className="px-screen-x py-3">
        {presets.length === 0 ? (
          <p className="py-4 text-center text-[13px] text-text-secondary">
            No presets yet. Add one in Settings → Quick Add Presets.
          </p>
        ) : (
          <div className="flex flex-col gap-2 pb-3">
            {presets.map((p) => {
              const cat = data.categories.find((c) => c.id === p.categoryId);
              const isIncome = p.transactionType === 'income';
              return (
                <motion.button
                  key={p.id}
                  type="button"
                  onClick={() => apply(p.id, p.label)}
                  whileTap={tapScale}
                  transition={springQuick}
                  className="flex items-center gap-3 rounded-card bg-surface-secondary px-3 py-3 text-left"
                >
                  <span
                    className={`w-9 h-9 rounded-button inline-flex items-center justify-center shrink-0 ${
                      isIncome
                        ? 'bg-positive/15 text-positive'
                        : 'bg-negative/15 text-negative'
                    }`}
                  >
                    {isIncome ? (
                      <ArrowUp size={16} strokeWidth={1.75} />
                    ) : (
                      <ArrowDown size={16} strokeWidth={1.75} />
                    )}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-medium text-text-primary truncate">
                      {p.label}
                    </span>
                    <span className="block text-[12px] text-text-secondary truncate">
                      {cat?.name ?? '—'}
                    </span>
                  </span>
                  <span className="text-[15px] font-mono tabular-nums text-text-primary shrink-0">
                    {formatCurrency(p.amount, { currency })}
                  </span>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
